import { format, parseISO } from 'date-fns'
import { LoaderCircle, RefreshCw, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  SYNC_LOOKAHEAD_MIN_DAYS,
  SYNC_LOOKBACK_MIN_DAYS,
  SYNC_WINDOW_MAX_DAYS,
} from '@/config/constants'
import { US_TIMEZONE_OPTIONS } from '@/utils/time'
import {
  cobaltLastSyncAt,
  formatCobaltSyncTime,
  nextCobaltSyncAt,
} from './cobaltSyncTiming'
import type {
  CobaltSyncConfig,
  CobaltSyncConfigUpdate,
  CobaltSyncEntity,
  CobaltSyncEntityState,
  CobaltSyncRun,
  CobaltSyncWindow,
} from './types'

const ACTIVE_COBALT_STATUSES = new Set(['queued', 'dispatching', 'running'])
const SCHEDULE_FREQUENCIES = [1, 2, 3, 4, 6, 8, 12, 24] as const
const SCHEDULE_HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const SCHEDULE_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  ...US_TIMEZONE_OPTIONS,
]
const SCHEDULE_FREQUENCY_OPTIONS = SCHEDULE_FREQUENCIES.map((frequency) => ({
  value: String(frequency),
  label: frequency === 1 ? 'Every hour' : `Every ${frequency} hours`,
}))
const EMPTY_SCHEDULE_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  []

function cobaltRunSummary(run: CobaltSyncRun): string {
  if (run.total_records === null) {
    return `${run.processed_records.toLocaleString()} records processed`
  }
  return `${run.processed_records.toLocaleString()} of ${run.total_records.toLocaleString()} records`
}

function cobaltStatusLabel(run: CobaltSyncRun): string {
  if (run.phase === 'complete_with_failures') return 'Completed with errors'
  if (run.status === 'succeeded') {
    return run.failed_records > 0 ? 'Completed with errors' : 'Completed'
  }
  if (run.status === 'failed') return 'Failed'
  if (run.status === 'cancelled') return 'Cancelled'
  if (run.status === 'queued') return 'Queued'
  return 'Running'
}

function formatScheduleHour(hour: number): string {
  const period = hour < 12 ? 'AM' : 'PM'
  return `${hour % 12 || 12}:00 ${period}`
}

function cobaltScheduleLabel(config: CobaltSyncConfig): string {
  const frequency =
    config.frequency_hours === 1
      ? 'Every hour'
      : `Every ${config.frequency_hours} hours`
  return `${frequency} · ${formatScheduleHour(config.start_hour)}–${formatScheduleHour(config.end_hour)} · ${config.timezone}`
}

function ScheduleSelect({
  label,
  ariaLabel,
  value,
  options,
  disabled,
  onValueChange,
}: {
  label: string
  ariaLabel: string
  value: string
  options: ReadonlyArray<{ value: string; label: string }>
  disabled: boolean
  onValueChange: (value: string) => void
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <Select value={value} disabled={disabled} onValueChange={onValueChange}>
        <SelectTrigger size="sm" className="w-full" aria-label={ariaLabel}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function cobaltAvailableRecordsLabel(
  recordCount: number | undefined,
  window: CobaltSyncWindow | null | undefined
): string {
  if (recordCount === undefined) return '— available'
  const count = `${recordCount.toLocaleString()} available`
  if (window === null || window === undefined) return count

  const start = parseISO(window.start_date)
  const end = parseISO(window.end_date)
  const sameYear = start.getFullYear() === end.getFullYear()
  const sameMonth = sameYear && start.getMonth() === end.getMonth()
  const dateRange = sameMonth
    ? `${format(start, 'MMM d')}–${format(end, 'd, yyyy')}`
    : sameYear
      ? `${format(start, 'MMM d')}–${format(end, 'MMM d, yyyy')}`
      : `${format(start, 'MMM d, yyyy')}–${format(end, 'MMM d, yyyy')}`
  return `${count} · ${dateRange}`
}

function hourOptionsFrom(hours: number[], current: number) {
  return (hours.includes(current) ? hours : [current, ...hours])
    .toSorted((a, b) => a - b)
    .map((hour) => ({
      value: String(hour),
      label: formatScheduleHour(hour),
    }))
}

interface CobaltEntityRowProps {
  entity: CobaltSyncEntity
  label: string
  config?: CobaltSyncConfig
  state?: CobaltSyncEntityState
  isLoading: boolean
  isError: boolean
  isConfigLoading: boolean
  isConfigError: boolean
  canManage: boolean
  isStarting: boolean
  isCancelling: boolean
  isUpdatingConfig: boolean
  onStart: (entity: CobaltSyncEntity) => void
  onCancel: (entity: CobaltSyncEntity) => void
  onUpdateConfig: (
    entity: CobaltSyncEntity,
    changes: CobaltSyncConfigUpdate
  ) => Promise<boolean>
}

export function CobaltEntityRow({
  entity,
  label,
  config,
  state,
  isLoading,
  isError,
  isConfigLoading,
  isConfigError,
  canManage,
  isStarting,
  isCancelling,
  isUpdatingConfig,
  onStart,
  onCancel,
  onUpdateConfig,
}: CobaltEntityRowProps) {
  const [dayError, setDayError] = useState<string | null>(null)
  // Bumped to remount the uncontrolled day input, putting the saved value back
  // after a rejected entry. Part of the input's key, so a save that changes
  // `dayValue` resets it too.
  const [dayResetToken, setDayResetToken] = useState(0)
  const run = state?.latest_run
  const isActive = !!run && ACTIVE_COBALT_STATUSES.has(run.status)
  const progress = run?.progress_percent ?? 0
  const recordCount = state?.available_records
  const status = isLoading
    ? 'Checking'
    : isError
      ? 'Unavailable'
      : run
        ? cobaltStatusLabel(run)
        : 'Never synced'
  const lastSyncAt = cobaltLastSyncAt(run)
  const nextSyncAt = nextCobaltSyncAt(config)
  const dayField =
    entity === 'referrals' || entity === 'orders'
      ? 'lookback_days'
      : entity === 'appointments' || entity === 'availability'
        ? 'lookahead_days'
        : undefined
  const dayLabel =
    dayField === 'lookback_days' ? 'Lookback days' : 'Lookahead days'
  const dayValue = dayField ? config?.[dayField] : undefined
  const minDayValue =
    dayField === 'lookback_days'
      ? SYNC_LOOKBACK_MIN_DAYS
      : SYNC_LOOKAHEAD_MIN_DAYS
  const defaultDayValue =
    dayField === 'lookback_days'
      ? config?.default_lookback_days
      : config?.default_lookahead_days
  const timezoneOptions = useMemo(
    () =>
      config &&
      !SCHEDULE_TIMEZONES.some(({ value }) => value === config.timezone)
        ? [
            { value: config.timezone, label: config.timezone },
            ...SCHEDULE_TIMEZONES,
          ]
        : SCHEDULE_TIMEZONES,
    [config]
  )
  // The window does not wrap (see the start/end check in
  // hoku/server/handlers/cobalt_connections.py), so each end of it only offers
  // hours that keep the pair ordered. A stored value outside that range still
  // has to appear, or the select would show a blank for what is saved.
  const startHourOptions = useMemo(
    () =>
      config
        ? hourOptionsFrom(
            SCHEDULE_HOURS.filter((hour) => hour <= config.end_hour),
            config.start_hour
          )
        : EMPTY_SCHEDULE_OPTIONS,
    [config]
  )
  const endHourOptions = useMemo(
    () =>
      config
        ? hourOptionsFrom(
            SCHEDULE_HOURS.filter((hour) => hour >= config.start_hour),
            config.end_hour
          )
        : EMPTY_SCHEDULE_OPTIONS,
    [config]
  )

  const updateDays = async (rawValue: string) => {
    if (!dayField || !config) return
    const trimmed = rawValue.trim()
    const value = trimmed === '' ? null : Number(trimmed)
    if (
      value !== null &&
      (!Number.isInteger(value) ||
        value < minDayValue ||
        value > SYNC_WINDOW_MAX_DAYS)
    ) {
      setDayError(
        `Enter a whole number from ${minDayValue} to ${SYNC_WINDOW_MAX_DAYS}, or leave blank for the default.`
      )
      setDayResetToken((token) => token + 1)
      return
    }
    setDayError(null)
    if (value === dayValue) return
    const saved = await onUpdateConfig(entity, { [dayField]: value })
    if (!saved) {
      setDayError('Not saved. The server rejected that value.')
      setDayResetToken((token) => token + 1)
    }
  }

  return (
    <div role="group" aria-label={label} className="space-y-3 p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-baseline gap-2">
            <p className="text-sm font-medium text-primary-high-contrast">
              {label}
            </p>
            <p className="text-xs font-medium text-primary-high-contrast">
              {cobaltAvailableRecordsLabel(recordCount, state?.window)}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">{status}</p>
          {(lastSyncAt || nextSyncAt) && (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              {lastSyncAt && (
                <span>
                  Last sync{' '}
                  <time dateTime={lastSyncAt.toISOString()}>
                    {formatCobaltSyncTime(lastSyncAt, config?.timezone)}
                  </time>
                </span>
              )}
              {nextSyncAt && (
                <span>
                  Next run{' '}
                  <time dateTime={nextSyncAt.toISOString()}>
                    {formatCobaltSyncTime(nextSyncAt, config?.timezone)}
                  </time>
                </span>
              )}
            </div>
          )}
        </div>
        {isActive ? (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={!canManage || isCancelling}
            onClick={() => onCancel(entity)}>
            {isCancelling ? <LoaderCircle className="animate-spin" /> : <X />}
            Cancel
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canManage || isLoading || isError || isStarting}
            onClick={() => onStart(entity)}>
            <RefreshCw className={isStarting ? 'animate-spin' : undefined} />
            Sync
          </Button>
        )}
      </div>

      {/* Say so rather than rendering nothing: without this, a failed config
          read is indistinguishable from an entity that has no schedule. */}
      {!config && (isConfigLoading || isConfigError) && (
        <p className="text-xs text-muted-foreground">
          {isConfigLoading
            ? 'Loading schedule…'
            : 'Schedule settings are unavailable.'}
        </p>
      )}
      {config && (
        <div className="space-y-3 rounded-md bg-muted/30 p-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="flex items-center gap-2 text-xs font-medium text-primary-high-contrast">
              <Switch
                checked={config.enabled}
                disabled={!canManage || isUpdatingConfig}
                aria-label={`${label} scheduled sync`}
                onCheckedChange={(enabled) =>
                  onUpdateConfig(entity, { enabled })
                }
              />
              Scheduled sync
            </Label>
            {config.enabled && (
              <p
                aria-label={`${label} sync schedule`}
                className="text-xs text-muted-foreground">
                {cobaltScheduleLabel(config)}
              </p>
            )}
          </div>
          {config.enabled && (
            <div className="grid grid-cols-2 gap-2 border-t border-neutral-subtle-border pt-2">
              <ScheduleSelect
                label="Timezone"
                ariaLabel={`${label} timezone`}
                value={config.timezone}
                options={timezoneOptions}
                disabled={!canManage || isUpdatingConfig}
                onValueChange={(timezone) =>
                  onUpdateConfig(entity, { timezone })
                }
              />
              <ScheduleSelect
                label="Frequency"
                ariaLabel={`${label} frequency`}
                value={String(config.frequency_hours)}
                options={SCHEDULE_FREQUENCY_OPTIONS}
                disabled={!canManage || isUpdatingConfig}
                onValueChange={(frequency) =>
                  onUpdateConfig(entity, {
                    frequency_hours: Number(frequency),
                  })
                }
              />
              {/* Both hours go in every update: the window is validated as a
                  pair, so the server rejects one on its own. */}
              <ScheduleSelect
                label="Start hour"
                ariaLabel={`${label} start hour`}
                value={String(config.start_hour)}
                options={startHourOptions}
                disabled={!canManage || isUpdatingConfig}
                onValueChange={(hour) =>
                  onUpdateConfig(entity, {
                    start_hour: Number(hour),
                    end_hour: config.end_hour,
                  })
                }
              />
              <ScheduleSelect
                label="End hour"
                ariaLabel={`${label} end hour`}
                value={String(config.end_hour)}
                options={endHourOptions}
                disabled={!canManage || isUpdatingConfig}
                onValueChange={(hour) =>
                  onUpdateConfig(entity, {
                    start_hour: config.start_hour,
                    end_hour: Number(hour),
                  })
                }
              />
            </div>
          )}
          {dayField &&
            defaultDayValue !== null &&
            defaultDayValue !== undefined && (
              <div className="flex flex-wrap items-end justify-between gap-2 border-t border-neutral-subtle-border pt-2">
                <div>
                  <p className="text-xs font-medium text-primary-high-contrast">
                    Sync window
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use the {defaultDayValue}-day default.
                  </p>
                  {/* Relay's availability endpoint resolves the lookback from
                      the request but the lookahead from its own constant, so a
                      saved lookahead changes what the sync reads without
                      changing the count shown above. Say so rather than let
                      the number look like it followed the setting. Remove this
                      once relay resolves both windows from the stored config
                      (entity_availability in relay cobalt_sync/service.py). */}
                  {dayField === 'lookahead_days' && (
                    <p className="text-xs text-muted-foreground">
                      Syncs use this window; the count above always covers{' '}
                      {defaultDayValue} days.
                    </p>
                  )}
                </div>
                <Label className="space-y-1 text-xs text-muted-foreground">
                  <span className="block">{dayLabel}</span>
                  <Input
                    key={`${entity}-${dayValue ?? 'default'}-${dayResetToken}`}
                    type="number"
                    min={minDayValue}
                    max={SYNC_WINDOW_MAX_DAYS}
                    step={1}
                    defaultValue={dayValue ?? ''}
                    placeholder={`Default (${defaultDayValue})`}
                    disabled={!canManage || isUpdatingConfig}
                    aria-label={`${label} ${dayLabel.toLowerCase()}`}
                    aria-invalid={dayError !== null}
                    className="h-8 w-32 text-xs"
                    onChange={() => {
                      if (dayError !== null) setDayError(null)
                    }}
                    onBlur={(event) =>
                      void updateDays(event.currentTarget.value)
                    }
                  />
                  {dayError !== null && (
                    <span role="alert" className="block text-destructive">
                      {dayError}
                    </span>
                  )}
                </Label>
              </div>
            )}
        </div>
      )}

      {!isLoading && !isError && run && run.status !== 'cancelled' && (
        <div className="space-y-1.5">
          <progress
            aria-label={`${label} sync progress`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            max={100}
            value={progress}
            className="h-2 w-full overflow-hidden rounded-full accent-primary"
          />
          <p className="text-xs text-muted-foreground">
            {cobaltRunSummary(run)}
          </p>
        </div>
      )}
    </div>
  )
}
